import {
  useQuery,
  useMutation,
  useQueryClient,
  noop
} from '@tanstack/react-query';
import * as networking from '../lib/networking';
import { RhinoContextType, RhinoRecordIdentifier } from '../index';

export const API_KEYS_PATH = '/api/auth/api_keys';
export const apiKeysQueryKey = ['apiKeys'];

export interface CreateApiKeyParams {
  name: string;
  full_access?: boolean;
  expires_at?: string | null;
}

export const useApiKeys = () =>
  useQuery({
    queryKey: apiKeysQueryKey,
    queryFn: () =>
      networking.networkApiCallOnlyData(API_KEYS_PATH, { method: 'get' })
  });

// Prefetch from a TanStack Router loader so the api keys list is in the React
// Query cache before <ApiKeysSettings> mounts. The key matches useApiKeys, so
// the component's useQuery deduplicates and renders without a loading spinner.
export const prefetchApiKeys = (rhino: RhinoContextType) =>
  rhino.queryClient
    .query({
      queryKey: apiKeysQueryKey,
      queryFn: () =>
        networking.networkApiCallOnlyData(API_KEYS_PATH, { method: 'get' })
    })
    .then(noop, noop);

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateApiKeyParams) =>
      networking.networkApiCallOnlyData(API_KEYS_PATH, {
        method: 'post',
        data: params
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: apiKeysQueryKey })
  });
};

export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: RhinoRecordIdentifier) =>
      networking.networkApiCallOnlyData(`${API_KEYS_PATH}/${id}`, {
        method: 'delete'
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: apiKeysQueryKey })
  });
};
